import * as React from 'react';

import { BodyCopy } from '@xing-com/typography';

import * as Styled from './dialog.styles';
import type { DialogPrimitiveProps } from './dialog.types';

export const DialogPrimitive = React.forwardRef<
  HTMLDivElement,
  DialogPrimitiveProps
>(
  (
    {
      cancelLabel,
      confirmLabel,
      headline,
      isDestructive,
      loading,
      onCancel,
      onConfirm,
      screenReaderDescription,
      showCancelAction,
      text,
      variant,
      ...props
    },
    forwardedRef
  ) => {
    const headlineId = React.useId();
    const textId = React.useId();
    const srDescriptionId = React.useId();

    const describedByIds = screenReaderDescription
      ? `${textId} ${srDescriptionId}`
      : textId;
    const bodyElement =
      typeof text === 'string' || typeof text === 'number' ? undefined : 'div';

    return (
      <Styled.Dialog
        aria-describedby={describedByIds}
        aria-labelledby={headline ? headlineId : undefined}
        aria-modal="true"
        data-xds="Dialog"
        ref={forwardedRef}
        role="alertdialog"
        tabIndex={-1}
        {...props}
      >
        <Styled.Content>
          {headline && (
            <Styled.Headline id={headlineId} size="medium">
              {headline}
            </Styled.Headline>
          )}
          <BodyCopy as={bodyElement} id={textId} size="medium" noMargin>
            {text}
          </BodyCopy>
          {screenReaderDescription && (
            <Styled.SrOnly className={srDescriptionId}>
              {screenReaderDescription}
            </Styled.SrOnly>
          )}
        </Styled.Content>
        <Styled.Footer $stacked={variant === 'stacked'}>
          {showCancelAction && (
            <Styled.Action
              $stacked={variant === 'stacked'}
              aria-label={cancelLabel}
              disabled={loading}
              fontWeight="regular"
              onClick={onCancel}
              size="medium"
            >
              {cancelLabel}
            </Styled.Action>
          )}
          <Styled.Action
            $destructive={isDestructive}
            $onlyOneButton={!showCancelAction}
            $stacked={variant === 'stacked'}
            aria-busy={loading ? 'true' : 'false'}
            aria-label={confirmLabel}
            loading={loading}
            onClick={onConfirm}
            size="medium"
          >
            {confirmLabel}
          </Styled.Action>
        </Styled.Footer>
      </Styled.Dialog>
    );
  }
);

DialogPrimitive.displayName = 'Dialog (Primitive)';
